import React, { useEffect, useState } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, Typography, Paper, Card, CardContent, Divider, useMediaQuery,
    Dialog, DialogTitle, DialogContent, DialogActions, Box,
    Tooltip,
    IconButton
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import baseURL from "../../config";
import { EditNote } from "@mui/icons-material";

const CrewingContracts = () => {
    const { id: crewingAgentId } = useParams();
    const [contracts, setContracts] = useState([]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [openModal, setOpenModal] = useState(false);
    const [modalDocuments, setModalDocuments] = useState([]);
    const [modalTitle, setModalTitle] = useState("");

    const navigate = useNavigate();

    const displayNames = {
        pipolicydocument: "PI Policy Document",
        maritimelabourconvention: "Maritime Labour Convention",
        fsdocument: "FS Document",
        dmlcp1: "DMLC Part I",
        dmlc2: "DMLC Part II",
    };

    const handleEdit = (contract) => {
        navigate(`/ContractTable/${crewingAgentId}/CrewingEditContractTable`, {
            state: {
                contract: contract,
                crewingAgentId: crewingAgentId,
            },
        });
    };

    const handleOpenHistory = (contractId) => {
        // Add your history modal logic here
        console.log("Opening history for contract:", contractId);
    };

    useEffect(() => {
        FetchCrewingContract();
    }, []);

    const FetchCrewingContract = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(
                `${baseURL}/api/getContractDetailsByAgent/${crewingAgentId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setContracts(Array.isArray(res.data.contracts) ? res.data.contracts : []);
        } catch (err) {
            console.error(err.response?.data || err.message);
        }
    };

    const handleOpenModal = (title, docs) => {
        setModalTitle(title);
        setModalDocuments(docs);
        setOpenModal(true);
    };

    // Card layout for mobile
    const renderMobileCards = () =>
        contracts.map((contract, index) => (
            <Card key={contract._id} sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="subtitle2"><b>Sr. No.:</b> {index + 1}</Typography>
                    <Divider sx={{ my: 1 }} />

                    {/* Vessel Owner */}
                    <Typography><b>Vessel:</b> {contract?.ShipDetails?.vesselname}</Typography>
                    <Typography><b>IMO Number:</b> {contract?.ShipDetails?.IMO_Number}</Typography>
                    <Typography><b>Ship Type:</b> {contract?.ShipDetails?.vesseltype}</Typography>
                    <Typography><b>Ship Flag:</b> {contract?.ShipDetails?.Flag}</Typography>
                    <Typography><b>GRT:</b> {contract?.ShipDetails?.gross_tonage}</Typography>
                    <Typography><b>KW:</b> {contract?.ShipDetails?.kiloWatt}</Typography>

                    <Button
                        variant="contained"
                        sx={{ alignSelf: "flex-start", mt: 1, backgroundColor: "#D4AF37" }}
                        onClick={() =>
                            handleOpenModal(
                                "Vessel Documents",
                                [
                                    "pipolicydocument",
                                    "maritimelabourconvention",
                                    "fsdocument",
                                    "dmlcp1",
                                    "dmlc2",
                                ].map((doc) => ({
                                    name: doc,
                                    filePath: contract?.ShipDetails?.[doc]?.filePath || null,
                                }))
                            )
                        }
                    >
                        Vessel Files
                    </Button>

                    <Divider sx={{ my: 1 }} />

                    {/* Crew */}
                    <Typography><b>Name:</b> {contract?.crewdetails?.name}</Typography>
                    <Typography><b>Indos No.:</b> {contract?.crewdetails?.indosno}</Typography>
                    <Typography><b>Email:</b> {contract?.crewdetails?.email}</Typography>
                    <Typography><b>Rank:</b> {contract?.crewdetails?.rankname}</Typography>
                    <Typography><b>Contract Duration:</b> {contract?.contractDuration}</Typography>
                    <Button
                        variant="contained"
                        sx={{ backgroundColor: "#D4AF37" }}
                        onClick={() =>
                            handleOpenModal(
                                "Crew Documents",
                                [
                                    { key: "crewDocument", label: "Crew Document" },
                                    { key: "passportDocument", label: "Passport" },
                                    { key: "cdcDocument", label: "CDC" },
                                    { key: "stcwDocument", label: "STCW" },
                                    { key: "stsdsdDocument", label: "STSDSD" },
                                    { key: "pscrbDocument", label: "PSCRB" },
                                    { key: "watchkeepingDocument", label: "Watchkeeping" },
                                    { key: "CopDocument", label: "COP" },
                                    { key: "yellowfeverDocument", label: "Yellow Fever" },
                                    { key: "CovidVaccineDocument", label: "COVID Vaccine" },
                                    { key: "iloDocument", label: "ILO" },
                                    { key: "sidDocument", label: "SID" },
                                    { key: "h2sDocument", label: "H2S" },
                                    { key: "CrewPhotoDocument", label: "Crew Photo" },
                                    { key: "cookcocDocument", label: "Cook COC" },
                                    { key: "officercocDocument", label: "Officer COC" },
                                    { key: "gocDocument", label: "GOC" },
                                    { key: "gmdssDocument", label: "GMDSS" },
                                    { key: "huetDocument", label: "HUET" },
                                    { key: "highvoltageDocument", label: "High Voltage" },
                                    { key: "brmDocument", label: "BRM" },
                                    { key: "degreeDiplomaDocument", label: "Degree/Diploma" },
                                    { key: "bosietDocument", label: "BOSIET" },
                                    { key: "signContratDocument", label: "Signed Contract" },
                                    { key: "AFF", label: "Advanced Fire Fighting (AFF)" },
                                    { key: "MFA", label: "Medical First Aid (MFA)" },
                                    { key: "PSSR", label: "Personal Safety and Social Responsibility (PSSR)" },
                                    { key: "SSO", label: "Ship Security Officer (SSO)" },
                                    { key: "BTM", label: "Bridge Team Management (BTM)" },
                                    { key: "ECDIS", label: "ECDIS" },
                                    { key: "contract", label: "Contract" },
                                    { key: "BankDocument", label: "Bank Details" },
                                ].map((doc) => ({
                                    name: doc.label,
                                    filePath: contract?.crewdetails?.[doc.key]?.filePath || null,
                                }))
                                    .filter((doc) => doc.filePath)
                            )
                        }
                    >
                        Crew Files
                    </Button>

                    <Divider sx={{ my: 1 }} />

                    {/* Travel Docs */}
                    <Button
                        variant="contained"
                        sx={{ backgroundColor: "#C94E17" }}
                        style={{
                            display: [
                                "BSM", "RPSL", "NOK", "DrugAlcoholPolicy",
                                "CheckList", "DeclarationForm", "TravelTicket",
                                "Visa", "LOG", "SignedContract"
                            ].some(type => contract[type]?.filePath) ? 'block' : 'none'
                        }}
                        onClick={() => {
                            const uploadedDocuments = [
                                { type: "BSM", color: "primary" },
                                { type: "RPSL", color: "secondary" },
                                { type: "NOK", color: "success" },
                                { type: "DrugAlcoholPolicy", color: "warning" },
                                { type: "CheckList", color: "info" },
                                { type: "DeclarationForm", color: "error" },
                                { type: "TravelTicket", color: "success" },
                                { type: "Visa", color: "warning" },
                                { type: "LOG", color: "info" },
                                { type: "SignedContract", color: "error" }
                            ].filter(file => contract[file.type]?.filePath);

                            handleOpenModal(
                                "Crewing Agent Documents",
                                uploadedDocuments.map(file => ({
                                    name: file.type,
                                    filePath: contract[file.type].filePath.startsWith("http")
                                        ? contract[file.type].filePath
                                        : `${baseURL}/${contract[file.type].filePath.replace(/\\/g, "/")}`
                                }))
                            );
                        }}
                    >
                        Documents
                    </Button>

                    <Divider sx={{ my: 1 }} />

                    {/* Status - Updated Section */}
                    <Typography sx={{ fontSize: "0.85rem" }}>
                        <strong>Admin Status:</strong>{" "}
                        {contract.contractStatus?.latestStatus?.adminStatus || "N/A"}
                    </Typography>

                    <Typography sx={{ fontSize: "0.85rem" }}>
                        <strong>Crewing Agent Status:</strong>{" "}
                        {contract.contractStatus?.latestStatus?.crewingAgentStatus || "N/A"}
                    </Typography>

                    {/* Show Animated Text if Crewing Agent Status is "Pending" */}
                    {contract.isApproved && (!contract.contractStatus?.latestStatus?.crewingAgentStatus ||
                        contract.contractStatus.latestStatus.crewingAgentStatus !== "Approve") && (
                        <Typography
                            sx={{
                                fontSize: "1rem",
                                color: "#FF4500",
                                fontWeight: "bold",
                                display: "flex",
                                alignItems: "center",
                                animation: "pulse 1.5s infinite"
                            }}
                        >
                            Agent Please approve contract
                            <ArrowForwardIcon sx={{ fontSize: "2rem", color: "#FF6347" }} />
                        </Typography>
                    )}

                    {contract.contractStatus?.latestStatus?.crewingAgentStatus !== "Pending" &&
                        contract.contractStatus?.latestStatus?.remark && (
                        <>
                            <Typography sx={{ fontSize: "0.85rem" }}>
                                <strong>Remark:</strong>{" "}
                                {contract.contractStatus.latestStatus.remark}
                            </Typography>
                            <Typography sx={{ fontSize: "0.85rem" }}>
                                <strong>Created At:</strong>{" "}
                                {contract.contractStatus.latestStatus.updatedAt
                                    ? new Date(
                                        contract.contractStatus.latestStatus.updatedAt
                                    ).toLocaleString()
                                    : "N/A"}
                            </Typography>
                        </>
                    )}

                    {contract?.contractStatus?.statusHistory?.length > 0 && (
                        <Button
                            sx={{ backgroundColor: '#E49B0F', color: '#fff', '&:hover': { backgroundColor: '#1d4ed8' }, mt: 1 }}
                            onClick={() => handleOpenHistory(contract._id)}>
                            View History
                        </Button>
                    )}

                    {contract.contractStatus?.formI?.filePath ? (
                        <Typography variant="body2" color="primary" sx={{ mt: 1, fontSize: "0.85rem" }}>
                            <a
                                href={
                                    contract.contractStatus.formI.filePath.startsWith("http")
                                        ? contract.contractStatus.formI.filePath
                                        : `${baseURL}/${contract.contractStatus.formI.filePath.replace(/\\/g, "/")}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                View Form I
                            </a>
                            {!crewingAgentId && (
                                <Typography sx={{ color: "red", fontWeight: "bold" }}>
                                    Form I uploaded. Proceed to Sign On/Sign Off for further processing.
                                </Typography>
                            )}
                        </Typography>
                    ) : contract.contractStatus?.latestStatus?.crewingAgentStatus === "Approve" ? (
                        <Typography
                            variant="body2"
                            color="error"
                            sx={{
                                fontSize: "1rem",
                                color: "#FF4500",
                                fontWeight: "bold",
                                display: "flex",
                                alignItems: "center",
                                animation: "pulse 1.5s infinite",
                                mt: 1
                            }}
                        >
                            Please add Form I
                            <ArrowForwardIcon sx={{ fontSize: "2rem", color: "#FF6347" }} />
                        </Typography>
                    ) : null}

                    <Divider sx={{ my: 1 }} />
                     <TableCell>
                                <Tooltip title="Update Status" arrow>
                                    <IconButton sx={{ color: "#AE0A10" }} onClick={() => handleEdit(contract)}>
                                        <EditNote />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>

                    {/* Add Animation in CSS */}
                    <style>
                        {`
                            @keyframes pulse {
                                0% { transform: scale(1); opacity: 1; }
                                50% { transform: scale(1.1); opacity: 0.8; }
                                100% { transform: scale(1); opacity: 1; }
                            }
                        `}
                    </style>
                </CardContent>
            </Card>
        ));

    // Table layout for desktop
    const renderDesktopTable = () => (
        <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
            <Table>
                <TableHead>
                    <TableRow sx={{ backgroundColor: "#AE0A10" }}>
                        <TableCell sx={{ color: "#fff" }}>Sr. No.</TableCell>
                        <TableCell sx={{ color: "#fff" }}>Vessel Owner</TableCell>
                        <TableCell sx={{ color: "#fff" }}>Crew</TableCell>
                        <TableCell sx={{ color: "#fff" }}>Travel Documents</TableCell>
                        <TableCell sx={{ color: "#fff" }}>Status</TableCell>
                        <TableCell sx={{ color: "#fff" }}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {contracts.map((contract, index) => (
                        <TableRow key={contract._id}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>
                                <Typography><b>Vessel:</b> {contract?.ShipDetails?.vesselname}</Typography>
                                <Typography><b>IMO Number:</b> {contract?.ShipDetails?.IMO_Number}</Typography>
                                <Typography><b>Ship Type:</b> {contract?.ShipDetails?.vesseltype}</Typography>
                                <Typography><b>Ship Flag:</b> {contract?.ShipDetails?.Flag}</Typography>
                                <Typography><b>GRT:</b> {contract?.ShipDetails?.gross_tonage}</Typography>
                                <Typography><b>KW:</b> {contract?.ShipDetails?.kiloWatt}</Typography>

                                <Button
                                    variant="contained"
                                    sx={{ alignSelf: "flex-start", mt: 1, backgroundColor: "#D4AF37" }}
                                    onClick={() =>
                                        handleOpenModal(
                                            "Vessel Documents",
                                            [
                                                "pipolicydocument",
                                                "maritimelabourconvention",
                                                "fsdocument",
                                                "dmlcp1",
                                                "dmlc2",
                                            ].map((doc) => ({
                                                name: doc,
                                                filePath: contract?.ShipDetails?.[doc]?.filePath || null,
                                            }))
                                        )
                                    }
                                >
                                    Vessel Files
                                </Button>
                            </TableCell>

                            <TableCell>
                                <Typography><b>Name:</b> {contract?.crewdetails?.name}</Typography>
                                <Typography><b>Indos No.:</b> {contract?.crewdetails?.indosno}</Typography>
                                <Typography><b>Email:</b> {contract?.crewdetails?.email}</Typography>
                                <Typography><b>Rank:</b> {contract?.crewdetails?.rankname}</Typography>
                                <Typography><b>Contract Duration:</b> {contract?.contractDuration}</Typography>
                                <Button
                                    variant="contained"
                                    sx={{ backgroundColor: "#D4AF37" }}
                                    onClick={() =>
                                        handleOpenModal(
                                            "Crew Documents",
                                            [
                                                { key: "crewDocument", label: "Crew Document" },
                                                { key: "passportDocument", label: "Passport" },
                                                { key: "cdcDocument", label: "CDC" },
                                                { key: "stcwDocument", label: "STCW" },
                                                { key: "stsdsdDocument", label: "STSDSD" },
                                                { key: "pscrbDocument", label: "PSCRB" },
                                                { key: "watchkeepingDocument", label: "Watchkeeping" },
                                                { key: "CopDocument", label: "COP" },
                                                { key: "yellowfeverDocument", label: "Yellow Fever" },
                                                { key: "CovidVaccineDocument", label: "COVID Vaccine" },
                                                { key: "iloDocument", label: "ILO" },
                                                { key: "sidDocument", label: "SID" },
                                                { key: "h2sDocument", label: "H2S" },
                                                { key: "CrewPhotoDocument", label: "Crew Photo" },
                                                { key: "cookcocDocument", label: "Cook COC" },
                                                { key: "officercocDocument", label: "Officer COC" },
                                                { key: "gocDocument", label: "GOC" },
                                                { key: "gmdssDocument", label: "GMDSS" },
                                                { key: "huetDocument", label: "HUET" },
                                                { key: "highvoltageDocument", label: "High Voltage" },
                                                { key: "brmDocument", label: "BRM" },
                                                { key: "degreeDiplomaDocument", label: "Degree/Diploma" },
                                                { key: "bosietDocument", label: "BOSIET" },
                                                { key: "signContratDocument", label: "Signed Contract" },
                                                { key: "AFF", label: "Advanced Fire Fighting (AFF)" },
                                                { key: "MFA", label: "Medical First Aid (MFA)" },
                                                { key: "PSSR", label: "Personal Safety and Social Responsibility (PSSR)" },
                                                { key: "SSO", label: "Ship Security Officer (SSO)" },
                                                { key: "BTM", label: "Bridge Team Management (BTM)" },
                                                { key: "ECDIS", label: "ECDIS" },
                                                { key: "contract", label: "Contract" },
                                                { key: "BankDocument", label: "Bank Details" },
                                            ].map((doc) => ({
                                                name: doc.label,
                                                filePath: contract?.crewdetails?.[doc.key]?.filePath || null,
                                            }))
                                                .filter((doc) => doc.filePath)
                                        )
                                    }
                                >
                                    Crew Files
                                </Button>
                            </TableCell>

                            <TableCell>
                                <Button
                                    variant="contained"
                                    sx={{ backgroundColor: "#C94E17" }}
                                    style={{
                                        display: [
                                            "BSM", "RPSL", "NOK", "DrugAlcoholPolicy",
                                            "CheckList", "DeclarationForm", "TravelTicket",
                                            "Visa", "LOG", "SignedContract"
                                        ].some(type => contract[type]?.filePath) ? 'block' : 'none'
                                    }}
                                    onClick={() => {
                                        const uploadedDocuments = [
                                            { type: "BSM", color: "primary" },
                                            { type: "RPSL", color: "secondary" },
                                            { type: "NOK", color: "success" },
                                            { type: "DrugAlcoholPolicy", color: "warning" },
                                            { type: "CheckList", color: "info" },
                                            { type: "DeclarationForm", color: "error" },
                                            { type: "TravelTicket", color: "success" },
                                            { type: "Visa", color: "warning" },
                                            { type: "LOG", color: "info" },
                                            { type: "SignedContract", color: "error" }
                                        ].filter(file => contract[file.type]?.filePath);

                                        handleOpenModal(
                                            "Crewing Agent Documents",
                                            uploadedDocuments.map(file => ({
                                                name: file.type,
                                                filePath: contract[file.type].filePath.startsWith("http")
                                                    ? contract[file.type].filePath
                                                    : `${baseURL}/${contract[file.type].filePath.replace(/\\/g, "/")}`
                                            }))
                                        );
                                    }}
                                >
                                    Documents
                                </Button>
                            </TableCell>

                            {/* Status - Updated Desktop Table Cell */}
                            <TableCell>
                                <Typography sx={{ fontSize: "0.85rem" }}>
                                    <strong>Admin Status:</strong>{" "}
                                    {contract.contractStatus?.latestStatus?.adminStatus || "N/A"}
                                </Typography>

                                <Typography sx={{ fontSize: "0.85rem" }}>
                                    <strong>Crewing Agent Status:</strong>{" "}
                                    {contract.contractStatus?.latestStatus?.crewingAgentStatus || "N/A"}
                                </Typography>

                                {/* Show Animated Text if Crewing Agent Status is "Pending" */}
                                {contract.isApproved && (!contract.contractStatus?.latestStatus?.crewingAgentStatus ||
                                    contract.contractStatus.latestStatus.crewingAgentStatus !== "Approve") && (
                                    <Typography
                                        sx={{
                                            fontSize: "1rem",
                                            color: "#FF4500",
                                            fontWeight: "bold",
                                            display: "flex",
                                            alignItems: "center",
                                            animation: "pulse 1.5s infinite"
                                        }}
                                    >
                                        Agent Please approve contract
                                        <ArrowForwardIcon sx={{ fontSize: "2rem", color: "#FF6347" }} />
                                    </Typography>
                                )}

                                {contract.contractStatus?.latestStatus?.crewingAgentStatus !== "Pending" &&
                                    contract.contractStatus?.latestStatus?.remark && (
                                    <>
                                        <Typography sx={{ fontSize: "0.85rem" }}>
                                            <strong>Remark:</strong>{" "}
                                            {contract.contractStatus.latestStatus.remark}
                                        </Typography>
                                        <Typography sx={{ fontSize: "0.85rem" }}>
                                            <strong>Created At:</strong>{" "}
                                            {contract.contractStatus.latestStatus.updatedAt
                                                ? new Date(
                                                    contract.contractStatus.latestStatus.updatedAt
                                                ).toLocaleString()
                                                : "N/A"}
                                        </Typography>
                                    </>
                                )}

                                {contract?.contractStatus?.statusHistory?.length > 0 && (
                                    <Button
                                        sx={{ backgroundColor: '#E49B0F', color: '#fff', '&:hover': { backgroundColor: '#1d4ed8' } }}
                                        onClick={() => handleOpenHistory(contract._id)}>
                                        View History
                                    </Button>
                                )}

                                {contract.contractStatus?.formI?.filePath ? (
                                    <Typography variant="body2" color="primary" sx={{ mt: 1, fontSize: "0.85rem" }}>
                                        <a
                                            href={
                                                contract.contractStatus.formI.filePath.startsWith("http")
                                                    ? contract.contractStatus.formI.filePath
                                                    : `${baseURL}/${contract.contractStatus.formI.filePath.replace(/\\/g, "/")}`
                                            }
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            View Form I
                                        </a>
                                        {!crewingAgentId && (
                                            <Typography sx={{ color: "red", fontWeight: "bold" }}>
                                                Form I uploaded. Proceed to Sign On/Sign Off for further processing.
                                            </Typography>
                                        )}
                                    </Typography>
                                ) : contract.contractStatus?.latestStatus?.crewingAgentStatus === "Approve" ? (
                                    <Typography
                                        variant="body2"
                                        color="error"
                                        sx={{
                                            fontSize: "1rem",
                                            color: "#FF4500",
                                            fontWeight: "bold",
                                            display: "flex",
                                            alignItems: "center",
                                            animation: "pulse 1.5s infinite"
                                        }}
                                    >
                                        Please add Form I
                                        <ArrowForwardIcon sx={{ fontSize: "2rem", color: "#FF6347" }} />
                                    </Typography>
                                ) : null}

                                {/* Add Animation in CSS */}
                                <style>
                                    {`
                                        @keyframes pulse {
                                            0% { transform: scale(1); opacity: 1; }
                                            50% { transform: scale(1.1); opacity: 0.8; }
                                            100% { transform: scale(1); opacity: 1; }
                                        }
                                    `}
                                </style>
                            </TableCell>
                            
                            <TableCell>
                                <Tooltip title="Update Status" arrow>
                                    <IconButton sx={{ color: "#AE0A10" }} onClick={() => handleEdit(contract)}>
                                        <EditNote />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <>
            {isMobile ? renderMobileCards() : renderDesktopTable()}

            {/* Documents Modal */}
            <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth>
                <DialogTitle>{modalTitle}</DialogTitle>
                <DialogContent>
                    {modalDocuments.length > 0 ? (
                        modalDocuments.map((doc, index) => (
                            <Paper
                                key={index}
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    p: 2,
                                    mb: 2,
                                }}
                            >
                                <Box display="flex" alignItems="center">
                                    <PictureAsPdfIcon sx={{ color: "red", mr: 1 }} />
                                    <Typography>{displayNames[doc.name] || doc.name}</Typography>
                                </Box>
                                {doc.filePath ? (
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        sx={{
                                            borderColor: "#AD0A11",
                                            color: "#AD0A11",
                                            "&:hover": { borderColor: "#8A090E", color: "#8A090E" },
                                        }}
                                        href={doc.filePath.startsWith("http") ? doc.filePath : `${baseURL}/${doc.filePath.replace(/\\/g, "/")}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Download
                                    </Button>
                                ) : (
                                    <Box display="flex" alignItems="center">
                                        <ReportProblemIcon sx={{ color: "orange", mr: 1 }} />
                                        <Typography color="error">No File Available</Typography>
                                    </Box>
                                )}
                            </Paper>
                        ))
                    ) : (
                        <Typography textAlign="center" color="textSecondary">
                            No documents available
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenModal(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CrewingContracts;